import React, {
  useCallback,
  useEffect,
  useState
} from "react";

import Step1OTP from "./Step1OTP";
import Step2Personal from "./Step2Personal";
import Step3Aadhaar from "./Step3Aadhaar";
import Step4Nominee from "./Step4Nominee";
import Step5Bank from "./Step5Bank";
import Step6Review from "./Step6Review";

import ProgressBar from "./ProgressBar";

import "./Registration.css";


/* ==========================================================
   REGISTRATION STORAGE KEYS
========================================================== */

const REGISTRATION_STORAGE_KEYS = [

  /* ========================================================
     STEP 1
  ======================================================== */

  "registrationStep1",
  "mobileNumber",
  "otp",
  "enteredOtp",
  "generatedOtp",
  "otpVerified",
  "termsAccepted",


  /* ========================================================
     STEP 2
  ======================================================== */

  "registrationStep2",


  /* ========================================================
     STEP 3
  ======================================================== */

  "registrationStep3",

  "aadhaarFile",
  "aadhaarFileName",

  "aadhaarFront",
  "aadhaarFrontName",

  "aadhaarBack",
  "aadhaarBackName",

  "passportPhoto",
  "passportPhotoName",

  "casteCertificate",
  "casteCertificateName",

  "otherDocument",
  "otherDocumentName",


  /* ========================================================
     STEP 4
  ======================================================== */

  "registrationStep4",
  "nomineeData",


  /* ========================================================
     STEP 5
  ======================================================== */

  "registrationStep5",

  "bankData",

  "accountHolderName",
  "accountNumber",
  "ifscCode",
  "bankName",
  "bankBranch",
  "accountType",


  /* ========================================================
     COMPLETE REGISTRATION
  ======================================================== */

  "registrationData",
  "formData",


  /* ========================================================
     PAYMENT
  ======================================================== */

  "paymentData",
  "paymentDetails",
  "paymentAmount",
  "paymentStatus",

  "paymentId",
  "transactionId",
  "orderId",

  "razorpayPaymentId",
  "razorpayOrderId",


  /* ========================================================
     MEMBERSHIP
  ======================================================== */

  "membershipId",
  "membershipValidTill",
  "membershipStatus",

  "membershipData",
  "memberData",


  /* ========================================================
     REGISTRATION FLAGS
  ======================================================== */

  "registrationStarted",
  "registrationCompleted",
  "paymentStarted",
  "paymentCompleted",
  "registrationInProgress",

];


/* ==========================================================
   OLD / LEGACY REGISTRATION KEYS

   These are included so older versions of the application
   cannot bring old user information back.
========================================================== */

const OLD_REGISTRATION_KEYS = [

  "registration",
  "registrationInfo",

  "userRegistration",
  "userRegistrationData",

  "userData",

  "memberRegistration",
  "memberRegistrationData",

  "applicationData",
  "applicationDetails",

  "paymentInfo",
  "paymentResponse",

  "upiData",
  "upiDetails",

];


/* ==========================================================
   CLEAR OLD REGISTRATION DATA
========================================================== */

const clearOldRegistrationData = () => {

  console.log(
    "=========================================="
  );

  console.log(
    "STARTING COMPLETELY NEW REGISTRATION"
  );

  console.log(
    "Clearing previous registration data..."
  );

  console.log(
    "=========================================="
  );


  /* ========================================================
     ALL CURRENT REGISTRATION KEYS
     LOCAL STORAGE
  ======================================================== */

  REGISTRATION_STORAGE_KEYS.forEach((key) => {

    try {

      localStorage.removeItem(key);

    } catch (error) {

      console.error(
        `Unable to remove localStorage key: ${key}`,
        error
      );

    }

  });


  /* ========================================================
     ALL CURRENT REGISTRATION KEYS
     SESSION STORAGE
  ======================================================== */

  REGISTRATION_STORAGE_KEYS.forEach((key) => {

    try {

      sessionStorage.removeItem(key);

    } catch (error) {

      console.error(
        `Unable to remove sessionStorage key: ${key}`,
        error
      );

    }

  });


  /* ========================================================
     OLD / LEGACY KEYS
  ======================================================== */

  OLD_REGISTRATION_KEYS.forEach((key) => {

    try {

      localStorage.removeItem(key);

    } catch (error) {

      console.error(
        `Unable to remove old localStorage key: ${key}`,
        error
      );

    }


    try {

      sessionStorage.removeItem(key);

    } catch (error) {

      console.error(
        `Unable to remove old sessionStorage key: ${key}`,
        error
      );

    }

  });


  /* ========================================================
     IMPORTANT

     The new-registration flag is removed LAST.

     This prevents it from accidentally being removed before
     Registration.js has detected it.
  ======================================================== */

  try {

    sessionStorage.removeItem(
      "startNewRegistration"
    );

  } catch (error) {

    console.error(
      "Unable to remove startNewRegistration flag:",
      error
    );

  }


  console.log(
    "=========================================="
  );

  console.log(
    "OLD REGISTRATION DATA CLEARED"
  );

  console.log(
    "NEW REGISTRATION IS READY"
  );

  console.log(
    "=========================================="
  );

};


/* ==========================================================
   CHECK NEW REGISTRATION FLAG
========================================================== */

const isStartingNewRegistration = () => {

  try {

    return (
      sessionStorage.getItem(
        "startNewRegistration"
      ) === "true"
    );

  } catch (error) {

    console.error(
      "Unable to check new registration flag:",
      error
    );

    return false;

  }

};


/* ==========================================================
   GET INITIAL FORM DATA
========================================================== */

const getInitialFormData = () => {

  /* ========================================================
     NEW REGISTRATION

     NEVER LOAD OLD DATA.
  ======================================================== */

  if (
    isStartingNewRegistration()
  ) {

    console.log(
      "NEW REGISTRATION: Starting with empty form."
    );

    return {};

  }


  /* ========================================================
     NORMAL REGISTRATION

     Load current user's saved information so data survives
     Step 1 → Step 2 → Step 3 → Step 4 → Step 5 → Step 6.
  ======================================================== */

  try {

    const saved =
      localStorage.getItem(
        "registrationData"
      );


    if (!saved) {

      return {};

    }


    const parsed =
      JSON.parse(saved);


    if (
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed)
    ) {

      return parsed;

    }

  } catch (error) {

    console.error(
      "Unable to load registration data:",
      error
    );

  }


  return {};

};


/* ==========================================================
   REGISTRATION COMPONENT
========================================================== */

function Registration() {


  /* ========================================================
     CURRENT STEP
  ======================================================== */

  const [
    currentStep,
    setCurrentStep
  ] = useState(1);


  /* ========================================================
     EDIT MODE
  ======================================================== */

  const [
    editingStep,
    setEditingStep
  ] = useState(null);


  /* ========================================================
     FORM DATA

     IMPORTANT:

     If Register Now was clicked, this starts as {}.

     Therefore old values will NOT appear in Step 1 or Step 2.
  ======================================================== */

  const [
    formData,
    setFormData
  ] = useState(
    getInitialFormData
  );


  /* ========================================================
     NEW REGISTRATION INITIALIZATION
  ======================================================== */

  useEffect(() => {

    const newRegistration =
      isStartingNewRegistration();


    if (!newRegistration) {

      return;

    }


    console.log(
      "=========================================="
    );

    console.log(
      "NEW REGISTRATION DETECTED"
    );

    console.log(
      "Removing previous user's data..."
    );

    console.log(
      "=========================================="
    );


    /* ======================================================
       CLEAR OLD DATA
    ====================================================== */

    clearOldRegistrationData();


    /* ======================================================
       RESET FORM
    ====================================================== */

    setFormData({});


    /* ======================================================
       ALWAYS START AT STEP 1
    ====================================================== */

    setCurrentStep(1);


    /* ======================================================
       EXIT EDIT MODE
    ====================================================== */

    setEditingStep(null);


  }, []);


  /* ========================================================
     SAVE DATA
  ======================================================== */

  const saveData = useCallback(
    (data = {}) => {

      if (
        !data ||
        typeof data !== "object" ||
        Array.isArray(data)
      ) {

        return;

      }


      setFormData(
        (previousData) => {

          const updatedData = {

            ...previousData,

            ...data,

          };


          /* ================================================
             SAVE CURRENT REGISTRATION

             This is NOT clearing old data.

             It only saves the CURRENT user's data.
          ================================================= */

          try {

            localStorage.setItem(
              "registrationData",
              JSON.stringify(
                updatedData
              )
            );

          } catch (error) {

            console.error(
              "Unable to save registration data:",
              error
            );

          }


          return updatedData;

        }
      );

    },
    []
  );


  /* ========================================================
     UPDATE FORM DATA
  ======================================================== */

  const updateFormData =
    useCallback(
      (data = {}) => {

        saveData(data);

      },
      [saveData]
    );


  /* ========================================================
     SCROLL TO TOP
  ======================================================== */

  const scrollTop =
    useCallback(
      () => {

        window.scrollTo({

          top: 0,

          behavior: "smooth",

        });

      },
      []
    );


  /* ========================================================
     EDIT STEP
  ======================================================== */

  const handleEdit =
    useCallback(
      (step) => {

        const targetStep =
          Number(step);


        if (
          ![2, 3, 4, 5].includes(
            targetStep
          )
        ) {

          console.error(
            "Invalid edit step:",
            targetStep
          );

          return;

        }


        console.log(
          "EDIT STEP:",
          targetStep
        );


        setEditingStep(
          targetStep
        );


        setCurrentStep(
          targetStep
        );


        setTimeout(() => {

          scrollTop();

        }, 50);

      },
      [scrollTop]
    );


  /* ========================================================
     NEXT STEP
  ======================================================== */

  const nextStep =
    useCallback(
      (data = {}) => {

        /* ==================================================
           SAVE CURRENT USER'S DATA
        ================================================== */

        if (
          data &&
          typeof data === "object" &&
          !Array.isArray(data) &&
          Object.keys(data).length > 0
        ) {

          saveData(data);

        }


        /* ==================================================
           EDIT MODE
        ================================================== */

        if (
          editingStep !== null
        ) {

          console.log(
            "EDIT COMPLETE:",
            editingStep
          );


          setEditingStep(null);


          /* ----------------------------------------------
             Return to Review
          ---------------------------------------------- */

          setCurrentStep(6);


          setTimeout(() => {

            scrollTop();

          }, 50);


          return;

        }


        /* ==================================================
           NORMAL REGISTRATION
        ================================================== */

        setCurrentStep(
          (previousStep) => {

            if (
              previousStep < 6
            ) {

              return (
                previousStep + 1
              );

            }


            return 6;

          }
        );


        setTimeout(() => {

          scrollTop();

        }, 50);

      },
      [
        editingStep,
        saveData,
        scrollTop,
      ]
    );


  /* ========================================================
     PREVIOUS STEP
  ======================================================== */

  const prevStep =
    useCallback(
      () => {

        /* ==================================================
           EDIT MODE

           Return to Review instead of going backwards.
        ================================================== */

        if (
          editingStep !== null
        ) {

          setEditingStep(null);

          setCurrentStep(6);


          setTimeout(() => {

            scrollTop();

          }, 50);


          return;

        }


        /* ==================================================
           NORMAL BACK
        ================================================== */

        setCurrentStep(
          (previousStep) => {

            if (
              previousStep > 1
            ) {

              return (
                previousStep - 1
              );

            }


            return 1;

          }
        );


        setTimeout(() => {

          scrollTop();

        }, 50);

      },
      [
        editingStep,
        scrollTop,
      ]
    );


  /* ========================================================
     RENDER STEP
  ======================================================== */

  const renderStep = () => {

    switch (
      currentStep
    ) {


      /* ====================================================
         STEP 1
      ==================================================== */

      case 1:

        return (

          <Step1OTP

            nextStep={
              nextStep
            }

            onNext={
              nextStep
            }

            formData={
              formData
            }

            updateFormData={
              updateFormData
            }

          />

        );


      /* ====================================================
         STEP 2
      ==================================================== */

      case 2:

        return (

          <Step2Personal

            nextStep={
              nextStep
            }

            onNext={
              nextStep
            }

            prevStep={
              prevStep
            }

            onBack={
              prevStep
            }

            formData={
              formData
            }

            updateFormData={
              updateFormData
            }

            editingStep={
              editingStep
            }

            isEditing={
              editingStep === 2
            }

          />

        );


      /* ====================================================
         STEP 3
      ==================================================== */

      case 3:

        return (

          <Step3Aadhaar

            nextStep={
              nextStep
            }

            onNext={
              nextStep
            }

            prevStep={
              prevStep
            }

            onBack={
              prevStep
            }

            formData={
              formData
            }

            updateFormData={
              updateFormData
            }

            editingStep={
              editingStep
            }

            isEditing={
              editingStep === 3
            }

          />

        );


      /* ====================================================
         STEP 4
      ==================================================== */

      case 4:

        return (

          <Step4Nominee

            nextStep={
              nextStep
            }

            onNext={
              nextStep
            }

            prevStep={
              prevStep
            }

            onBack={
              prevStep
            }

            formData={
              formData
            }

            updateFormData={
              updateFormData
            }

            editingStep={
              editingStep
            }

            isEditing={
              editingStep === 4
            }

          />

        );


      /* ====================================================
         STEP 5
      ==================================================== */

      case 5:

        return (

          <Step5Bank

            nextStep={
              nextStep
            }

            onNext={
              nextStep
            }

            prevStep={
              prevStep
            }

            onBack={
              prevStep
            }

            formData={
              formData
            }

            updateFormData={
              updateFormData
            }

            editingStep={
              editingStep
            }

            isEditing={
              editingStep === 5
            }

          />

        );


      /* ====================================================
         STEP 6
      ==================================================== */

      case 6:

        return (

          <Step6Review

            formData={
              formData
            }

            onEdit={
              handleEdit
            }

            editStep={
              handleEdit
            }

            prevStep={
              prevStep
            }

            onBack={
              prevStep
            }

          />

        );


      /* ====================================================
         DEFAULT
      ==================================================== */

      default:

        return null;

    }

  };


  /* ========================================================
     MAIN
  ======================================================== */

  return (

    <div className="registration-page">

      {/* ====================================================
          PROGRESS BAR
      ==================================================== */}

      <ProgressBar
        currentStep={
          currentStep
        }
      />


      {/* ====================================================
          REGISTRATION CONTENT
      ==================================================== */}

      <div className="registration-container">

        {renderStep()}

      </div>

    </div>

  );

}


export default Registration;